import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import styles from "./supplierAwardList.module.scss";

interface SupplierAwardListProps {
  title?: string;
  data?: any[];
  supplierKey?: string;
  valueKey?: string;
  poCountKey?: string;
  valueFormatter?: (value: number) => string;
}

const SupplierAwardList: React.FC<SupplierAwardListProps> = ({
  title = "Award by Suppliers",
  data = [],
  supplierKey = "supplier",
  valueKey = "value",
  poCountKey = "poCount",
  valueFormatter,
}) => {
  const defaultColor = "#00d2aa";

  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];

    return data.map((item, index) => ({
      supplier: item[supplierKey] || `Supplier ${index + 1}`,
      value: Number(item[valueKey] || 0),
      poCount: Number(item[poCountKey] || 0),
    }));
  }, [data, supplierKey, valueKey, poCountKey]);

  const maxValue = useMemo(() => {
    if (chartData.length === 0) return 100;
    return Math.max(...chartData.map((item) => item.value), 1);
  }, [chartData]);

  const formatValue = (value: number) => {
    if (valueFormatter) return valueFormatter(value);
    if (value >= 1000000) return `$${(value / 1000000).toFixed(2)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value}`;
  };

  if (!chartData || chartData.length === 0) {
    return (
      <Box className={styles.container}>
        <Box className={styles.header}>
          <Typography variant="h6" className={styles.title}>
            {title}
          </Typography>
        </Box>
        <Box
          className={styles.listContainer}
          display="flex"
          alignItems="center"
          justifyContent="center"
          minHeight={200}
        >
          <Typography variant="body2" color="textSecondary">
            No data available
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={styles.container}>
      <Box className={styles.header}>
        <Typography variant="h6" className={styles.title}>
          {title}
        </Typography>
      </Box>

      <Box className={styles.listContainer}>
        {chartData.map((item, index) => (
          <Box key={index} className={styles.listItem}>
            <Box className={styles.supplierInfo}>
              <Typography className={styles.supplierName}>
                {item.supplier}
              </Typography>
              <Typography className={styles.poCount}>
                {item.poCount} POs
              </Typography>
            </Box>
            <Box className={styles.barSection}>
              <Box className={styles.barBackground}>
                <Box
                  className={styles.barFill}
                  style={{
                    width: `${(item.value / maxValue) * 100}%`,
                    background: defaultColor,
                  }}
                />
              </Box>
            </Box>
            <Typography className={styles.value}>
              {formatValue(item.value)}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default SupplierAwardList;
